import { Component, Inject, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatChipInputEvent } from '@angular/material/chips';
import { COMMA, ENTER, TAB } from '@angular/cdk/keycodes';
import { Cinema } from '../../../../models/cinema';
import { CinemaService } from '../../../../services/cinema.service';
import { ToastService } from '../../../../services/toast.service';
import { DialogService } from '../../../../services/dialog.service';
import { getStarIconClass } from '../../../shared/components/star-rating/star-rating.util';

@Component({
  selector: 'app-admin-cinema',
  templateUrl: './admin-cinema.component.html',
  styleUrls: ['./admin-cinema.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AdminCinemaComponent implements OnInit {

  form!: FormGroup;
  editando = false;
  salvando = false;
  extraindo = false;
  urlAdoroCinema = '';

  dataSource!: MatTableDataSource<Cinema>;

  elencoTags: string[] = [];
  readonly separatorKeysCodes = [ENTER, COMMA, TAB] as const;

  thumbnailFile?: File;
  imagemGrandeFile?: File;
  thumbnailPreview?: string;
  imagemGrandePreview?: string;

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  displayedColumns: string[] = ['thumbnail', 'titulo', 'anoLancamento', 'direcao', 'actions'];

  readonly stars = [1, 2, 3, 4, 5];

  constructor(
    private fb: FormBuilder,
    private service: CinemaService,
    private toastService: ToastService,
    private dialogService: DialogService,
    @Inject('BASE_URL') private baseUrl: string,
  ) { }

  ngOnInit(): void {
    this.buildForm();
    this.load();
  }

  buildForm(): void {
    this.form = this.fb.group({
      id: [0],
      titulo: ['', Validators.required],
      tituloOriginal: [''],
      descricaoCurta: ['', Validators.required],
      descricao: [''],
      anoLancamento: [new Date().getFullYear(), Validators.required],
      direcao: [''],
      thumbnail: [''],
      imagemGrande: [''],
      avaliacao: [null]
    });
  }

  load(): void {
    this.service.getAll().subscribe(res => {
      this.dataSource = new MatTableDataSource(res);
      this.dataSource.sort = this.sort;
      this.dataSource.paginator = this.paginator;
    });
  }

  editar(filme: Cinema): void {
    this.editando = true;
    this.form.patchValue(filme);
    this.elencoTags = this.parseElenco(filme.elenco);
    this.thumbnailPreview = this.getImagemUrl(filme.thumbnail);
    this.imagemGrandePreview = this.getImagemUrl(filme.imagemGrande);
    this.thumbnailFile = undefined;
    this.imagemGrandeFile = undefined;
  }

  novo(): void {
    this.editando = false;
    this.form.reset({
      id: 0,
      titulo: '',
      tituloOriginal: '',
      descricaoCurta: '',
      descricao: '',
      anoLancamento: new Date().getFullYear(),
      direcao: '',
      thumbnail: '',
      imagemGrande: '',
      avaliacao: null
    });
    this.elencoTags = [];
    this.thumbnailFile = undefined;
    this.imagemGrandeFile = undefined;
    this.thumbnailPreview = undefined;
    this.imagemGrandePreview = undefined;
  }

  async extrair(): Promise<void> {
    const url = this.urlAdoroCinema.trim();
    if (!url) return;

    this.extraindo = true;

    try {
      const filme = await firstValueFrom(this.service.extrair(url));
      this.form.patchValue({
        titulo: filme.titulo,
        tituloOriginal: filme.tituloOriginal ?? '',
        direcao: filme.direcao ?? '',
        descricao: filme.descricao ?? ''
      });
      if (filme.anoLancamento) {
        this.form.patchValue({ anoLancamento: filme.anoLancamento });
      }
      this.elencoTags = [...filme.elenco];
      this.toastService.show('Informações extraídas', { type: 'success' });
    } catch (err: any) {
      const msg = typeof err?.error === 'string' ? err.error : 'Erro ao extrair informações!';
      this.toastService.show(msg, { type: 'danger' });
    } finally {
      this.extraindo = false;
    }
  }

  addTag(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();

    if (value && !this.elencoTags.includes(value)) {
      this.elencoTags.push(value);
    }

    event.chipInput.clear();
  }

  removeTag(tag: string): void {
    this.elencoTags = this.elencoTags.filter(t => t !== tag);
  }

  onStarClick(event: MouseEvent, star: number): void {
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const clickedLeftHalf = (event.clientX - rect.left) < rect.width / 2;
    const valor = clickedLeftHalf ? star - 0.5 : star;

    const atual = this.form.get('avaliacao')?.value;
    this.form.patchValue({ avaliacao: atual === valor ? null : valor });
  }

  starIconClass(position: number): string {
    return getStarIconClass(position, this.form?.get('avaliacao')?.value || 0);
  }

  onThumbnailChange(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      this.thumbnailFile = file;
      this.thumbnailPreview = URL.createObjectURL(file);
    }
  }

  onImagemGrandeChange(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      this.imagemGrandeFile = file;
      this.imagemGrandePreview = URL.createObjectURL(file);
    }
  }

  async salvar(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.salvando = true;

    try {
      if (this.thumbnailFile) {
        const res = await firstValueFrom(this.service.upload(this.thumbnailFile));
        this.form.patchValue({ thumbnail: res.fileServerName });
      }

      if (this.imagemGrandeFile) {
        const res = await firstValueFrom(this.service.upload(this.imagemGrandeFile));
        this.form.patchValue({ imagemGrande: res.fileServerName });
      }

      const data: Cinema = { ...this.form.value, elenco: this.elencoTags.join(', ') };

      if (this.editando) {
        await firstValueFrom(this.service.update(data));
        this.toastService.show('Editado com sucesso', { type: 'success' });
      } else {
        await firstValueFrom(this.service.create(data));
        this.toastService.show('Criado com sucesso', { type: 'success' });
      }

      this.load();
      this.novo();
    } catch {
      this.toastService.show('Erro ao salvar!', { type: 'danger' });
    } finally {
      this.salvando = false;
    }
  }

  async remover(id: number): Promise<void> {
    const confirmado = await this.dialogService.confirm('Deseja realmente excluir?', 'Tem certeza?');

    if (confirmado) {
      this.service.delete(id).subscribe({
        next: () => {
          this.toastService.show('Excluído com sucesso', { type: 'success' });
          this.load();
        },
        error: () => {
          this.toastService.show('Erro ao excluir!', { type: 'danger' });
        }
      });
    }
  }

  getImagemUrl(fileServerName?: string): string {
    if (!fileServerName) return '';

    if (/^https?:\/\//i.test(fileServerName)) return fileServerName;

    const normalizedBaseUrl = this.baseUrl.endsWith('/') ? this.baseUrl : `${this.baseUrl}/`;
    return `${normalizedBaseUrl}uploads/cinema/${fileServerName}`;
  }

  private parseElenco(elenco?: string): string[] {
    return (elenco || '').split(',').map(s => s.trim()).filter(Boolean);
  }
}
